import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { supabase, reportSupabaseError } from '@/lib/supabase';
import { uploadDocumentResumable as uploadToStorage } from '@/lib/storage';
import { extractPDFOutline, extractTextFromPDF, isPDF } from '@/lib/pdf';
import { toast } from '@/components/ui/Toast';
import type { Document } from '@/types';

interface DocumentsState {
  documents: Document[];
  selectedDocumentIds: string[];
  isLoading: boolean;
  error: string | null;
  setDocuments: (documents: Document[]) => void;
  addDocument: (document: Document) => void;
  updateDocument: (id: string, updates: Partial<Document>) => void;
  removeDocument: (id: string) => void;
  toggleDocumentSelection: (id: string) => void;
  clearSelection: () => void;
  setLoading: (loading: boolean) => void;
  fetchDocuments: (userId: string) => Promise<void>;
  uploadDocument: (file: File, userId: string, onProgress?: (percent: number) => void) => Promise<Document | null>;
  deleteDocument: (id: string) => Promise<void>;
  renameDocument: (id: string, newTitle: string) => Promise<void>;
}

function getDocumentType(ext: string): Document['type'] {
  const typeMap: Record<string, Document['type']> = {
    pdf: 'pdf',
    docx: 'docx',
    doc: 'docx',
    pptx: 'pptx',
    ppt: 'pptx',
    png: 'image',
    jpg: 'image',
    jpeg: 'image',
    gif: 'image',
    webp: 'image',
    mp3: 'audio',
    wav: 'audio',
    m4a: 'audio',
    mp4: 'video',
    webm: 'video',
    mov: 'video',
  };
  return typeMap[ext] || 'pdf';
}

export const useDocumentsStore = create<DocumentsState>((set, get) => ({
  documents: [],
  selectedDocumentIds: [],
  isLoading: false,
  error: null,
  setDocuments: (documents) => set({ documents }),
  addDocument: (document) =>
    set((state) => ({ documents: [...state.documents, document] })),
  updateDocument: (id, updates) =>
    set((state) => ({
      documents: state.documents.map((doc) =>
        doc.id === id ? { ...doc, ...updates } : doc
      ),
    })),
  removeDocument: (id) =>
    set((state) => ({
      documents: state.documents.filter((doc) => doc.id !== id),
      selectedDocumentIds: state.selectedDocumentIds.filter((docId) => docId !== id),
    })),
  toggleDocumentSelection: (id) =>
    set((state) => ({
      selectedDocumentIds: state.selectedDocumentIds.includes(id)
        ? state.selectedDocumentIds.filter((docId) => docId !== id)
        : [...state.selectedDocumentIds, id],
    })),
  clearSelection: () => set({ selectedDocumentIds: [] }),
  setLoading: (isLoading) => set({ isLoading }),

  fetchDocuments: async (userId: string) => {
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      reportSupabaseError(error, 'fetchDocuments');
      set({ error: error.message, isLoading: false });
      return;
    }

    // Map DB field names to frontend types (file_type -> type, extract outline from metadata)
    const documents = (data || []).map((doc: Record<string, unknown>) => ({
      ...doc,
      type: doc.file_type,
      // Extract outline from metadata if present
      outline: (doc.metadata as Record<string, unknown> | null)?.outline || undefined,
    })) as Document[];

    set({ documents, isLoading: false });
  },

  uploadDocument: async (file: File, userId: string, onProgress?: (percent: number) => void) => {
    Sentry.addBreadcrumb({ category: 'document', message: `Upload started: ${file.name}`, level: 'info' });
    const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB limit

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      console.error(`File too large: ${sizeMB}MB (max 2GB)`);
      toast.error(`File too large: ${sizeMB}MB. Maximum allowed is 2GB.`);
      return null;
    }

    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const fileType = getDocumentType(fileExt || '');

    // Check session before upload
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('No active session - cannot upload');
      toast.error('Session expired. Please log in again.');
      return null;
    }

    console.log('Uploading to Supabase Storage:', { fileName: file.name, size: file.size, type: fileType });

    return Sentry.startSpan(
      { name: 'document.upload', op: 'task', attributes: { 'document.type': fileType, 'document.size': file.size } },
      async () => {
        // Upload to Supabase Storage
        let uploadResult;
        try {
          uploadResult = await Sentry.startSpan(
            { name: 'storage.upload', op: 'http.client' },
            () => uploadToStorage(file, userId, onProgress)
          );
        } catch (uploadError) {
          console.error('Storage upload error:', uploadError);
          toast.error(`Upload failed: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
          return null;
        }

        // Create document record in Supabase
        const newDoc = {
          user_id: userId,
          title: file.name.replace(/\.[^/.]+$/, ''),
          file_type: fileType,
          file_url: uploadResult.publicUrl,
          file_size: file.size,
          status: 'uploading',
        };

        const { data, error } = await Sentry.startSpan(
          { name: 'document.createRecord', op: 'db' },
          () => supabase.from('documents').insert(newDoc).select().single()
        );

        if (error) {
          reportSupabaseError(error, 'uploadDocument.insert');
          return null;
        }

        // Map DB field to frontend type
        const document = { ...data, type: data.file_type } as Document;
        get().addDocument(document);

        // Trigger Cloudflare Worker to process the document
        // Worker routes: PDFs → Vercel (more memory), others → Supabase edge function
        const sizeMB = (file.size / 1024 / 1024).toFixed(1);
        console.log(`[store] Document uploaded (${sizeMB}MB), triggering processing...`);
        toast.info(`Document uploaded! Processing started.`);

        // Get file key from upload result
        const fileKey = uploadResult.fileKey;

        // For PDFs, extract text and outline client-side
        // This preserves page numbers and gets reliable ToC from embedded bookmarks
        let pdfOutline = null;
        let extractedText = '';
        if (isPDF(file)) {
          try {
            console.log('[store] Extracting PDF content client-side...');
            const textResult = await Sentry.startSpan(
              { name: 'pdf.extractText', op: 'task' },
              () => extractTextFromPDF(file)
            );
            extractedText = textResult.text;
            console.log(`[store] Extracted ${extractedText.length} chars from ${textResult.pageCount} pages`);

            // Also extract outline for ToC/chapters
            pdfOutline = await extractPDFOutline(file);
            if (pdfOutline) {
              console.log(`[store] Extracted outline with ${pdfOutline.entries.length} entries`);
            }
          } catch (err) {
            console.warn('[store] Failed to extract PDF content:', err);
            // Continue without client extraction - server will try its own extraction
          }
        }

        // Trigger document processing via Supabase Edge Function
        supabase.functions.invoke('process-document', {
          body: {
            documentId: document.id,
            fileKey,
            // Include client-extracted text and outline if available
            extractedText: extractedText || undefined,
            outline: pdfOutline,
          },
        }).catch((err) => {
          Sentry.captureException(err, { tags: { context: 'triggerProcessDocument' } });
        });

        return document;
      }
    );
  },

  renameDocument: async (id: string, newTitle: string) => {
    const title = newTitle.trim();
    if (!title) return;

    Sentry.addBreadcrumb({ category: 'document', message: `Rename: ${id}`, level: 'info' });

    const previousTitle = get().documents.find((d) => d.id === id)?.title;
    get().updateDocument(id, { title });

    const { error } = await supabase
      .from('documents')
      .update({ title, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      reportSupabaseError(error, 'renameDocument');
      toast.error('Failed to rename document. Please try again.');
      if (previousTitle !== undefined) {
        get().updateDocument(id, { title: previousTitle });
      }
      throw error;
    }
  },

  deleteDocument: async (id: string) => {
    Sentry.addBreadcrumb({ category: 'document', message: `Delete: ${id}`, level: 'info' });
    const doc = get().documents.find((d) => d.id === id);
    if (!doc) return;

    // Delete from storage (extract path from URL)
    const urlParts = doc.file_url.split('/');
    const filePath = urlParts.slice(-2).join('/');
    await supabase.storage.from('documents').remove([filePath]);

    // Delete from database
    await supabase.from('documents').delete().eq('id', id);

    get().removeDocument(id);
  },
}));
